import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const SiteCtx = createContext(null);
const AuthCtx = createContext(null);

const FONT_MAP = {
  "Cormorant Garamond": "'Cormorant Garamond', Georgia, serif",
  "Cinzel": "'Cinzel', serif",
  "Playfair Display": "'Playfair Display', serif",
  "Lora": "'Lora', serif",
  "Plus Jakarta Sans": "'Plus Jakarta Sans', sans-serif",
  "Lato": "'Lato', sans-serif",
  "Source Sans 3": "'Source Sans 3', sans-serif",
  "Nunito Sans": "'Nunito Sans', sans-serif",
};

export function applyBranding(branding) {
  if (!branding) return;
  const r = document.documentElement;
  r.style.setProperty("--brand-primary", branding.primaryColor || "#0B1D3A");
  r.style.setProperty("--brand-accent", branding.accentColor || "#D4AF37");
  r.style.setProperty("--brand-bg", branding.bgColor || "#FBF9F5");
  r.style.setProperty("--brand-text", branding.textColor || "#22304A");
  r.style.setProperty("--brand-btn", branding.buttonColor || "#D4AF37");
  r.style.setProperty("--brand-btn-text", branding.buttonTextColor || "#0B1D3A");
  r.style.setProperty("--font-heading", FONT_MAP[branding.headingFont] || branding.headingFont || "'Cormorant Garamond', serif");
  r.style.setProperty("--font-body", FONT_MAP[branding.bodyFont] || branding.bodyFont || "'Plus Jakarta Sans', sans-serif");
  r.style.setProperty("--btn-radius", branding.buttonStyle === "sharp" ? "4px" : branding.buttonStyle === "rounded" ? "14px" : "999px");
  r.style.setProperty("--section-pad", branding.spacingPreset === "compact" ? "3.5rem" : branding.spacingPreset === "spacious" ? "7.5rem" : "5.5rem");
  r.setAttribute("data-hscale", branding.headingScale || "medium");
  if (branding.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSite = useCallback(async () => {
    try {
      const { data } = await api.get("/public/site");
      setSite(data);
      applyBranding(data.branding);
    } catch (e) {
      console.error("site load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshSite(); }, [refreshSite]);

  return <SiteCtx.Provider value={{ site, loading, refreshSite }}>{children}</SiteCtx.Provider>;
}

export function useSite() {
  return useContext(SiteCtx) || { site: null, loading: false, refreshSite: () => {} };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, false=logged out

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
